import create from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { ProfileSignalRQueryOperation, ProfileSignalRQuery } from '../types/api/profile';
import { env, isDevelopment } from '@diana/env-ui';
import { ISubscription } from '@microsoft/signalr/src/Stream';
import { useMessageStore } from './useMessageStore';
import { useErrorBannerStore } from './useErrorBannerStore';


interface HubState {
  stream: ISubscription<{}> | null;
  connection: HubConnection | null;
  streamMethod: ProfileSignalRQueryOperation;
  updating?: boolean;
  streamArgs: ProfileSignalRQuery | null;
  streamTimeout: NodeJS.Timeout | null; // Timer to clear stream messages after a certain time
  state?: HubConnectionState;
  isDisposed?: boolean;
  // Intent flag: the hub should be subscribed. Set when a subscription is
  // requested (even if the socket isn't connected yet), cleared on disposeStream.
  // Honored by initializeHub / onreconnected to (re)apply the subscription once
  // the connection is ready — including streams whose streamArgs are null.
  wantsSubscription?: boolean;
}

export interface StoreState {
  hubs: Record<string, HubState>;
  setStreamMethod: (hubName: string, method: ProfileSignalRQueryOperation) => void;
  setUpdating: (hubName: string, updating: boolean) => void;
  setStreamArgs: (hubName: string, args: ProfileSignalRQuery | null) => void;
  initializeHub: (hubName: string, url: string, getAccessToken: () => string) => Promise<void>;
  cleanUpHubConnection: (hubName: string) => Promise<void>;
  subscribeToStream: (hubName: string, method?: ProfileSignalRQueryOperation) => void;
  invokeMethod: (hubName: string, method: string, ...args: any[]) => Promise<any>;
  setNonStreamArgs: (hubName: string, args: ProfileSignalRQuery) => void;
  disposeStream: (hubName: string) => void;
}

export enum HubsNames {
  bunkeringDashboard = 'bunkeringDashboard',
  mfmComparsion = 'mfmComparsion',
  activeSessionBunkeringDashboard = 'activeSessionBunkeringDashboard',
  aggregateDashboard = 'aggregateDashboard',
  deliverySummary = 'deliverySummary',
  deliverySummaryRightDrawer = 'deliverySummaryRightDrawer',
  bunkeringOverview = 'bunkeringOverview',
  aggregateOverview = 'aggregateOverview',
  bunkeringRawData = 'bunkeringRawData',
  alarmsSummary = 'alarmsSummary',
  alarmsOverview = 'alarmsOverview',
  filterOptions = 'filterOptions',
  mpaStatusLogs = 'mpaStatusLogs',
  mpaStatusSubmit = 'mpaStatusSubmit',
  bunkeringStatusLogs = 'bunkeringStatusLogs',
}

export const useSignalRHubStore = create<StoreState>(
  devtools(
    (set, get) => {
      const urlConnections: Record<
        string,
        { connection: HubConnection | null; pending: Promise<void> | null }
      > = {};

      return {
        hubs: {},

        setStreamMethod: (hubName: string, method: ProfileSignalRQueryOperation) =>
          set(
            (state) => ({
              hubs: {
                ...state.hubs,
                [hubName]: {
                  ...state.hubs[hubName],
                  streamMethod: method,
                },
              },
            }),
            false,
            'setStreamMethod'
          ),

        setStreamArgs: (hubName: string, args: ProfileSignalRQuery | null) => {
          useMessageStore.getState().resetHubMessages(hubName);
          useMessageStore.getState().setStableLoadingFlag(hubName, true);
          set(
            (state) => ({
              hubs: {
                ...state.hubs,
                [hubName]: {
                  ...state.hubs[hubName],
                  streamArgs: args,
                  updating: true,
                },
              },
            }),
            false,
            'setStreamArgs'
          );

          get().hubs[hubName].streamTimeout = setTimeout(() => {
            useMessageStore.getState().setMessages(hubName, []);
          }, 2000);

          get().subscribeToStream(hubName, get().hubs[hubName].streamMethod);
        },

        setUpdating: (hubName: string, updating: boolean) => {
          set(
            (state) => ({
              hubs: {
                ...state.hubs,
                [hubName]: {
                  ...state.hubs[hubName],
                  updating,
                },
              },
            }),
            false,
            'setUpdating'
          );
        },

        initializeHub: async (hubName: string, url: string, getAccessToken: () => string) => {
          // Check if connection for this URL is either pending or already established
          if (
            urlConnections[url]?.connection &&
            urlConnections[url]?.connection?.state === HubConnectionState.Connected
          ) {
            console.log(`Reusing existing connection for ${url} (hub: ${hubName})`);

            // Set the existing connection for this hub
            set(
              (state) => ({
                hubs: {
                  ...state.hubs,
                  [hubName]: {
                    ...state.hubs[hubName],
                    connection: urlConnections[url].connection,
                  },
                },
              }),
              false,
              'reusingExistingConnection'
            );
            const reuseState = get().hubs[hubName];
            if (reuseState?.wantsSubscription && reuseState?.streamMethod) {
              get().subscribeToStream(hubName, reuseState.streamMethod);
              console.log(`Re-initialized hub ${hubName} (reused connection), resubscribed to stream.`);
            }
            return;
          }

          if (urlConnections[url]?.pending) {
            console.log(
              `Waiting for existing connection to be initialized for ${url} (hub: ${hubName})`,
            );

            // Wait for the pending connection to complete
            await urlConnections[url].pending;
            if (urlConnections[url]?.connection) {
              console.log(`Existing connection is now ready for reuse for ${hubName} with URL ${url}`);

              // Set the existing connection for this hub
              set(
                (state) => ({
                  hubs: {
                    ...state.hubs,
                    [hubName]: {
                      ...state.hubs[hubName],
                      connection: urlConnections[url].connection,
                    },
                  },
                }),
                false,
                'reusingExistingConnection'
              );
              const pendingState = get().hubs[hubName];
              if (pendingState?.wantsSubscription && pendingState?.streamMethod) {
                get().subscribeToStream(hubName, pendingState.streamMethod);
                console.log(`Re-initialized hub ${hubName} (pending connection), resubscribed to stream.`);
              }
              return;
            }
          }

          let resolvePending: () => void;

          // Mark this connection attempt as pending with a promise
          const pendingPromise = new Promise<void>((resolve) => {
            resolvePending = resolve;
          });
          urlConnections[url] = { connection: null, pending: pendingPromise };

          const connection = new HubConnectionBuilder()
            .withUrl(url, {
              accessTokenFactory: getAccessToken,
              skipNegotiation: true,
              transport: HttpTransportType.WebSockets,
            })
            .configureLogging(isDevelopment() ? LogLevel.Debug : LogLevel.Information)
            .withAutomaticReconnect({
              nextRetryDelayInMilliseconds: () => +env('SIGNALR_NEXT_RETRY_DELAY', 5000),
            })
            .build();

          connection.onreconnected(async (connectionId) => {
            console.log(`Reconnected to hub ${hubName} with connection ID: ${connectionId}`);

            // Resubscribe to the stream after reconnection
            const hubState = get().hubs[hubName];
            if (hubState?.wantsSubscription && hubState?.streamMethod) {
              get().subscribeToStream(hubName, hubState.streamMethod);
              console.log(`Resubscribed to stream for ${hubName} after reconnection.`);
            }
          });

          try {
            if (!getAccessToken()) {
              console.warn('User is not authenticated, not connecting to hub');
              // @ts-ignore
              resolvePending();
              return;
            }

            // Start the connection and mark it as ready once started
            await connection.start();
            console.log(`${hubName} SignalR connected to ${url}`);

            // Store the new connection and mark it as no longer pending
            urlConnections[url] = { connection, pending: null };
            // @ts-ignore
            resolvePending();

            // Update the hub's state with the new connection
            set(
              (state) => ({
                hubs: {
                  ...state.hubs,
                  [hubName]: {
                    ...state.hubs[hubName],
                    connection,
                  },
                },
              }),
              false,
              'initializeHub'
            );

            // If streamArgs already exist, this is a re-initialization (e.g. after token refresh).
            // Re-subscribe so data resumes without the consuming component needing to call setStreamArgs again.
            const existingHubState = get().hubs[hubName];
            if (existingHubState?.wantsSubscription && existingHubState?.streamMethod) {
              get().subscribeToStream(hubName, existingHubState.streamMethod);
              console.log(`Re-initialized hub ${hubName}, resubscribed to stream.`);
            }
          } catch (err) {
            console.error(`Error while starting SignalR connection for ${hubName} to ${url}: `, err);
            // Reset the pending state if the connection fails
            urlConnections[url].pending = null;
            set(
              (state) => ({
                hubs: {
                  ...state.hubs,
                  [hubName]: {
                    ...state.hubs[hubName],
                    state: HubConnectionState.Disconnected,
                  },
                },
              }),
              false,
              'setDisconnectedState'
            );
            // @ts-ignore
            resolvePending();
          }
        },

        cleanUpHubConnection: async (hubName: string) => {
          const state = get();
          const hubState = state.hubs[hubName];
          if (
            hubState &&
            hubState.connection &&
            hubState.connection.state !== HubConnectionState.Disconnected
          ) {
            await hubState.connection.stop();
          }
        },

        subscribeToStream: (hubName: string, method?: ProfileSignalRQueryOperation) => {
          const state = get();
          const hubState = state.hubs[hubName];
          if (hubState?.stream) {
            console.log(`[${hubName}] disposing previous subscription`);
            hubState.stream.dispose();
          }

          // If the hub isn't connected yet (e.g. right after logout/login, before
          // the socket finishes (re)connecting), record the intent to subscribe so
          // initializeHub / onreconnected re-applies it once connected. Without this
          // the subscription is silently dropped and only a page refresh recovers it.
          if (
            hubState &&
            (!hubState.connection ||
              hubState.connection.state !== HubConnectionState.Connected)
          ) {
            set(
              (s) => ({
                hubs: {
                  ...s.hubs,
                  [hubName]: {
                    ...s.hubs[hubName],
                    stream: null,
                    streamMethod: method || s.hubs[hubName]?.streamMethod,
                    wantsSubscription: true,
                  },
                },
              }),
              false,
              'deferSubscribeUntilConnected'
            );
            return;
          }

          if (
            hubState &&
            hubState.connection &&
            hubState.connection.state === HubConnectionState.Connected
          ) {
            const subscriptionId = `${hubName}-${Date.now()}`;
            console.log(`[${hubName}] creating subscription ${subscriptionId}`);
            useMessageStore.getState().resetHubMessages(hubName);
            let disposed = false;
            const rawStream = (hubState.streamArgs === null
              ? hubState.connection.stream(method || hubState.streamMethod)
              : hubState.connection.stream(method || hubState.streamMethod, hubState.streamArgs))
              .subscribe({
                next: (item) => {
                  if (disposed) {
                    console.warn(`[${hubName}] dropped late message from disposed ${subscriptionId}`);
                    return;
                  }
                  console.log(`[${hubName}] next from ${subscriptionId}`);
                  useMessageStore.getState().addMessage(hubName, item);
                  if (hubState.streamTimeout) {
                    clearTimeout(hubState.streamTimeout);
                  }
                },
                complete: () => {
                  console.log(`${hubName}] stream completed (${subscriptionId})`);
                },
                error: (err) => {
                  if (disposed) {
                    return;
                  }
                  if (
                    err?.message?.includes('An error occurred on the server while streaming results.')
                  ) {
                    console.error('Streaming error on the server.');

                    set(
                      (state) => ({
                        hubs: {
                          ...state.hubs,
                          [hubName]: {
                            ...state.hubs[hubName],
                            updating: false,
                          },
                        },
                      }),
                      false,
                      'setStreamError'
                    );

                    useErrorBannerStore.getState().showError(
                      hubName,
                      () => get().subscribeToStream(hubName, get().hubs[hubName]?.streamMethod),
                    );
                  }
                },
              });

            const stream: ISubscription<{}> = {
              dispose: () => {
                disposed = true;
                rawStream.dispose();
              },
            };

            set(
              {
                hubs: {
                  ...state.hubs,
                  [hubName]: {
                    ...hubState,
                    streamMethod: method || hubState.streamMethod,
                    stream,
                    isDisposed: false,
                    updating: true,
                    wantsSubscription: true,
                  },
                },
              },
              false,
              'subscribeToStream'
            );
          }
        },

        invokeMethod: async (hubName: string, method: string, ...args: any[]) => {
          const state = get();
          const hubState = state.hubs[hubName];
          if (
            hubState &&
            hubState.connection &&
            hubState.connection.state === HubConnectionState.Connected
          ) {
            try {
              return await hubState.connection.invoke(method, ...args);
            } catch (err) {
              console.error(`Error while invoking method ${method} on ${hubName}: `, err);
            }
          }
        },

        setNonStreamArgs: (hubName: string, args: ProfileSignalRQuery) => {
          set(
            (state) => ({
              hubs: {
                ...state.hubs,
                [hubName]: {
                  ...state.hubs[hubName],
                  streamArgs: args,
                  updating: true,
                  loading: true,
                },
              },
            }),
            false,
            'setNonStreamArgs'
          );
        },

        disposeStream: (hubName: string) => {
          const state = get();
          const hubState = state.hubs[hubName];
          if (hubState && hubState.stream) {
            hubState.stream.dispose();
            useMessageStore.getState().resetHubMessages(hubName);
            useMessageStore.getState().setStableLoadingFlag(hubName, false);
            set(
              (state) => ({
                hubs: {
                  ...state.hubs,
                  [hubName]: {
                    ...state.hubs[hubName],
                    stream: null,
                    streamArgs: null,
                    isDisposed: true,
                    wantsSubscription: false,
                  },
                },
              }),
              false,
              'disposeStream'
            );
          }
        },
      };
    },
    'SignalR Store'
  )
);
